import { Pipe, PipeTransform } from '@angular/core'
import { Entity } from 'src/app/models/fund-details.model'

const LESS_THAN = -1
const GREATER_THAN = 1
const TEMP_RANGE = -100
const TEMP_RANGE_CLOSED = 10000000000000000000

@Pipe({
  name: 'sortBy',
  pure: true,
})
export class SortByPipe implements PipeTransform {
  // eslint-disable-next-line complexity
  private priceType: string
  // eslint-disable-next-line complexity
  transform(
    value: Entity[],
    column: string,
    direction: string,
    switchModel: boolean,
  ): Entity[] {
    if (value === undefined || value === null) {
      return []
    }
    if (switchModel !== undefined) {
      this.priceType = switchModel ? 'market' : 'regular'
    }
    if (direction === 'desc' && column === 'fundName') {
      return this.performAlphabeticalSort(value, direction)
    }

    value = this.performAlphabeticalSort(value, direction)

    switch (column) {
      case 'ticker':
        if (value && !value[0]?.profile?.is529) {
          value = this.performTickerSort(value, direction)
        }       
        break;
      case 'assetClass':
        value = this.performAssetClassSort(value, direction)
        break
      case 'risk':
        value = this.performRiskSort(value, direction)
        break
      case 'expenseRatio':
        value = this.performRatioSort(value, direction)
        break
      case 'sinceInception':
        value = this.performSinceInceptionSort(value, direction)
        break
      case 'secYield':
        value = this.performYieldPctSort(value, direction)
        break
      case 'ytd':
        value = this.performytdSort(value, direction)
        break
      case '1-yr':
        value = this.performOneYrPctSort(value, direction)
        break
      case '5-yr':
        value = this.performFiveYrPctSort(value, direction)
        break
      case '10-yr':
        value = this.performTenYrPctSort(value, direction)
        break
      case 'investmentMinimum':
        value = this.performInvestMinimumSort(value, direction)
        break
      case 'price':
        value = this.performPriceSort(value, direction)
        break
      case 'unitPrice':
        value = this.performUnitPriceSort(value, direction)
        break
      case 'changePrice':
        value = this.performChangePriceSort(value, direction)
        break
    }

    return value
  }

  getPriceType(): string {
    return this.priceType
  }
  
  performAlphabeticalSort(value: Entity[], direction: string): any {
    if (direction === 'desc') {
      return value
        .sort((a, b) =>
          a.profile?.longName
            ?.replace('Vanguard ', '')
            ?.localeCompare(b.profile.longName.replace('Vanguard ', '')),
        )
        .reverse()
    }
    return value.sort((a, b) =>
      a.profile?.longName
        ?.replace('Vanguard ', '')
        ?.localeCompare(b.profile.longName.replace('Vanguard ', '')),
    )
  }

  performTickerSort(value: Entity[], direction: string): any {
    if (direction === 'desc') {
      return value
        .sort((a, b) =>
          a.profile.ticker.localeCompare(b.profile.ticker),
        )
        .reverse()
    }
    return value.sort((a, b) =>
      a.profile.ticker.localeCompare(b.profile.ticker),
    )
  }

  performAssetClassSort(value: any, direction: string): any {
    if (direction === 'desc') {
      return value
        .sort((a, b) =>
          a.profile?.customizedStyle?.localeCompare(b.profile.customizedStyle),
        )
        .reverse()
    }

    return value.sort((a, b) =>
      a.profile?.customizedStyle?.localeCompare(b.profile.customizedStyle),
    )
  }

  performRiskSort(value: any, direction: string): any {
    if (direction === 'desc') {
      return value.sort((a, b) => a.risk?.code - b.risk?.code).reverse()
    }

    return value.sort((a, b) => a.risk?.code - b.risk?.code)
  }

  performRatioSort(value: any, direction: string): any {
    if (direction === 'desc') {
      return value
        .sort((a, b) => {
          let aExpenseRatio = a.profile?.expenseRatio
          let bExpenseRatio = b.profile?.expenseRatio
          if (aExpenseRatio === undefined) {
            aExpenseRatio = TEMP_RANGE
          }
          if (bExpenseRatio === undefined) {
            bExpenseRatio = TEMP_RANGE
          }
          return aExpenseRatio - bExpenseRatio
        })
        .reverse()
    }
    return value.sort((a, b) => {
      let aExpenseRatio = a.profile?.expenseRatio
      let bExpenseRatio = b.profile?.expenseRatio
      if (aExpenseRatio === undefined) {
        aExpenseRatio = TEMP_RANGE
      }
      if (bExpenseRatio === undefined) {
        bExpenseRatio = TEMP_RANGE
      }
      return aExpenseRatio - bExpenseRatio
    })
  }

  performSinceInceptionSort(value: Entity[], direction: string): Entity[] {
    return (
      value
        // eslint-disable-next-line complexity, max-statements
        .sort((a, b) => {
          const fundRetInceptionA =
            a.monthEndAvgAnnualRtn.fundReturn.sinceInceptionPct
          const marketRetInceptionA =
            a.monthEndAvgAnnualRtn.marketPriceFundReturn?.sinceInceptionPct

          const fundRetInceptionB =
            b.monthEndAvgAnnualRtn.fundReturn.sinceInceptionPct
          const marketRetInceptionB =
            b.monthEndAvgAnnualRtn.marketPriceFundReturn?.sinceInceptionPct

          let inceptionFinalA
          let inceptionFinalB

          if (a.profile.isETF) {
            inceptionFinalA = marketRetInceptionA
          } else if (!a.profile.isETF) {
            inceptionFinalA = fundRetInceptionA
          }

          if (b.profile.isETF) {
            inceptionFinalB = marketRetInceptionB
          } else if (!b.profile.isETF) {
            inceptionFinalB = fundRetInceptionB
          }

          if (typeof inceptionFinalA === 'undefined') {
            inceptionFinalA = TEMP_RANGE
          }
          if (typeof inceptionFinalB === 'undefined') {
            inceptionFinalB = TEMP_RANGE
          }

          if (direction === 'asc') {
            return parseFloat(inceptionFinalA) < parseFloat(inceptionFinalB)
              ? LESS_THAN
              : GREATER_THAN
          } else {
            return parseFloat(inceptionFinalB) < parseFloat(inceptionFinalA)
              ? LESS_THAN
              : GREATER_THAN
          }
        })
    )
  }

  performYieldPctSort(value: any, direction: string): any {
    return (
      value
        // eslint-disable-next-line complexity, max-statements
        .sort((a, b) => {
          let yieldPctA = a.yield.yieldPct
          let yieldPctB = b.yield.yieldPct
          if (typeof yieldPctA === 'undefined') {
            yieldPctA = TEMP_RANGE
          }
          if (typeof yieldPctB === 'undefined') {
            yieldPctB = TEMP_RANGE
          }

          if (direction === 'asc') {
            return parseFloat(yieldPctA) < parseFloat(yieldPctB)
              ? LESS_THAN
              : GREATER_THAN
          } else {
            return parseFloat(yieldPctB) < parseFloat(yieldPctA)
              ? LESS_THAN
              : GREATER_THAN
          }
        })
    )
  }

  performytdSort(value: any, direction: string): any {
    return (
      value
        // eslint-disable-next-line complexity, max-statements
        .sort((a, b) => {
          const marketPriceA = a.ytd.marketPrice
          const regularA = a.ytd.regular

          const marketPriceB = b.ytd.marketPrice
          const regularB = b.ytd.regular

          let ytdA
          let ytdB

          if (a.profile.isETF) {
            ytdA = marketPriceA
          } else {
            ytdA = regularA
          }

          if (b.profile.isETF) {
            ytdB = marketPriceB
          } else {
            ytdB = regularB
          }

          if (typeof ytdA === 'undefined') {
            ytdA = TEMP_RANGE
          }

          if (typeof ytdB === 'undefined') {
            ytdB = TEMP_RANGE
          }

          if (direction === 'asc') {
            return parseFloat(ytdA) < parseFloat(ytdB)
              ? LESS_THAN
              : GREATER_THAN
          } else {
            return parseFloat(ytdB) < parseFloat(ytdA)
              ? LESS_THAN
              : GREATER_THAN
          }
        })
    )
  }

  performOneYrPctSort(value: Entity[], direction: string): Entity[] {
    return (
      value
        // eslint-disable-next-line complexity, max-statements
        .sort((a, b) => {
          const fundOneYrA = a.monthEndAvgAnnualRtn.fundReturn.oneYrPct
          const marketOneYrA =
            a.monthEndAvgAnnualRtn.marketPriceFundReturn?.oneYrPct
          const fundOneYrB = b.monthEndAvgAnnualRtn.fundReturn.oneYrPct
          const marketOneYrB =
            b.monthEndAvgAnnualRtn.marketPriceFundReturn?.oneYrPct
          let oneYrA
          let oneYrB
          if (a.profile.isETF) {
            oneYrA = marketOneYrA
          } else if (!a.profile.isETF) {
            oneYrA = fundOneYrA
          }
          if (b.profile.isETF) {
            oneYrB = marketOneYrB
          } else if (!b.profile.isETF) {
            oneYrB = fundOneYrB
          }
          if (typeof oneYrA === 'undefined') {
            oneYrA = TEMP_RANGE
          }
          if (typeof oneYrB === 'undefined') {
            oneYrB = TEMP_RANGE
          }
          if (direction === 'asc') {
            return parseFloat(oneYrA) < parseFloat(oneYrB)
              ? LESS_THAN
              : GREATER_THAN
          } else {
            return parseFloat(oneYrB) < parseFloat(oneYrA)
              ? LESS_THAN
              : GREATER_THAN
          }
        })
    )
  }

  performFiveYrPctSort(value: Entity[], direction: string): Entity[] {
    return (
      value
        // eslint-disable-next-line complexity, max-statements
        .sort((a, b) => {
          const fundFiveYrA = a.monthEndAvgAnnualRtn.fundReturn.fiveYrPct
          const marketFiveYrA =
            a.monthEndAvgAnnualRtn.marketPriceFundReturn?.fiveYrPct
          const fundFiveYrB = b.monthEndAvgAnnualRtn.fundReturn.fiveYrPct
          const marketFiveYrB =
            b.monthEndAvgAnnualRtn.marketPriceFundReturn?.fiveYrPct
          let fiveYrA
          let fiveYrB
          if (a.profile.isETF) {
            fiveYrA = marketFiveYrA
          } else if (!a.profile.isETF) {
            fiveYrA = fundFiveYrA
          }
          if (b.profile.isETF) {
            fiveYrB = marketFiveYrB
          } else if (!b.profile.isETF) {
            fiveYrB = fundFiveYrB
          }
          if (typeof fiveYrA === 'undefined') {
            fiveYrA = TEMP_RANGE
          }
          if (typeof fiveYrB === 'undefined') {
            fiveYrB = TEMP_RANGE
          }
          if (direction === 'asc') {
            return parseFloat(fiveYrA) < parseFloat(fiveYrB)
              ? LESS_THAN
              : GREATER_THAN
          } else {
            return parseFloat(fiveYrB) < parseFloat(fiveYrA)
              ? LESS_THAN
              : GREATER_THAN
          }
        })
    )
  }

  performTenYrPctSort(value: Entity[], direction: string): Entity[] {
    return (
      value
        // eslint-disable-next-line complexity, max-statements
        .sort((a, b) => {
          const fundTenYrA = a.monthEndAvgAnnualRtn.fundReturn.tenYrPct
          const marketTenYrA =
            a.monthEndAvgAnnualRtn.marketPriceFundReturn?.tenYrPct
          const fundTenYrB = b.monthEndAvgAnnualRtn.fundReturn.tenYrPct
          const marketTenYrB =
            b.monthEndAvgAnnualRtn.marketPriceFundReturn?.tenYrPct
          let tenYrA
          let tenYrB
          if (a.profile.isETF) {
            tenYrA = marketTenYrA
          } else if (!a.profile.isETF) {
            tenYrA = fundTenYrA
          }
          if (b.profile.isETF) {
            tenYrB = marketTenYrB
          } else if (!b.profile.isETF) {
            tenYrB = fundTenYrB
          }
          if (typeof tenYrA === 'undefined') {
            tenYrA = TEMP_RANGE
          }
          if (typeof tenYrB === 'undefined') {
            tenYrB = TEMP_RANGE
          }
          if (direction === 'asc') {
            return parseFloat(tenYrA) < parseFloat(tenYrB)
              ? LESS_THAN
              : GREATER_THAN
          } else {
            return parseFloat(tenYrB) < parseFloat(tenYrA)
              ? LESS_THAN
              : GREATER_THAN
          }
        })
    )
  }
  performInvestMinimumSort(value: Entity[], direction: string): Entity[] {
    return (
      value
        // eslint-disable-next-line complexity, max-statements
        .sort((a, b) => {
          const minInvestA = a.dailyPrice.regular.price
          const minimumInvestmentA = a.minimum.general?.initial
          const minInvestB = b.dailyPrice.regular.price
          const minimumInvestmentB = b.minimum.general?.initial
          let invMinA
          let invMinB

          if (a.profile.isETF) {
            invMinA = minInvestA
          } else if (!a.profile.isETF) {
            invMinA = minimumInvestmentA
          }

          if (b.profile.isETF) {
            invMinB = minInvestB
          } else if (!b.profile.isETF) {
            invMinB = minimumInvestmentB
          }

          if (
            a.profile.fundFact.isClosed ||
            a.profile.fundFact.isClosedToNewInvestors
          ) {
            invMinA = TEMP_RANGE_CLOSED
          } else if (typeof invMinA === 'undefined') {
            invMinA = TEMP_RANGE
          }

          if (
            b.profile.fundFact.isClosed ||
            b.profile.fundFact.isClosedToNewInvestors
          ) {
            invMinB = TEMP_RANGE_CLOSED
          } else if (typeof invMinB === 'undefined') {
            invMinB = TEMP_RANGE
          }
          if (direction === 'asc') {
            return parseFloat(invMinA) < parseFloat(invMinB)
              ? LESS_THAN
              : GREATER_THAN
          } else {
            return parseFloat(invMinB) < parseFloat(invMinA)
              ? LESS_THAN
              : GREATER_THAN
          }
        })
    )
  }

  performPriceSort(value: Entity[], direction: string): Entity[] {
    return (
      value
        // eslint-disable-next-line complexity, max-statements
        .sort((a, b) => {
          let priceFinalA
          let priceFinalB
          if (a.profile.isETF || a.profile.isMutualFund) {
            priceFinalA = a.dailyPrice[this.priceType]?.price
          }
          if (b.profile.isETF || b.profile.isMutualFund) {
            priceFinalB = b.dailyPrice[this.priceType]?.price
          }
          if (typeof priceFinalA === 'undefined' || priceFinalA === '') {
            priceFinalA = TEMP_RANGE
          }
          if (typeof priceFinalB === 'undefined' || priceFinalB === '') {
            priceFinalB = TEMP_RANGE
          }
          if (direction === 'asc') {
            return parseFloat(priceFinalA) < parseFloat(priceFinalB)
              ? LESS_THAN
              : GREATER_THAN
          } else {
            return parseFloat(priceFinalB) < parseFloat(priceFinalA)
              ? LESS_THAN
              : GREATER_THAN
          }
        })
    )
  }

  performUnitPriceSort(value: Entity[], direction: string): Entity[] {
    return (
      value
        // eslint-disable-next-line complexity, max-statements
        .sort((a, b) => {
          let unitPriceA
          let unitPriceB

          if (a.profile.is529) {
            unitPriceA = a.dailyPrice.regular.price
          } else {
            unitPriceA = ''
          }
          if (b.profile.is529) {
            unitPriceB = b.dailyPrice.regular.price
          } else {
            unitPriceB = ''
          }
          if (typeof unitPriceA === 'undefined' || unitPriceA === '') {
            unitPriceA = TEMP_RANGE
          }
          if (typeof unitPriceB === 'undefined' || unitPriceB === '') {
            unitPriceB = TEMP_RANGE
          }
          if (direction === 'asc') {
            return parseFloat(unitPriceA) < parseFloat(unitPriceB)
              ? LESS_THAN
              : GREATER_THAN
          } else {
            return parseFloat(unitPriceB) < parseFloat(unitPriceA)
              ? LESS_THAN
              : GREATER_THAN
          }
        })
    )
  }

  performChangePriceSort(value: Entity[], direction: string): Entity[] {
    return (
      value
        // eslint-disable-next-line complexity, max-statements
        .sort((a, b) => {
          let changePriceA
          let changePriceB

          if (a.profile.isETF || a.profile.isMutualFund) {
            changePriceA = a.dailyPrice[this.priceType]?.priceChangeAmount
          } else if (a.profile.is529) {
            changePriceA = a.dailyPrice.regular.priceChangeAmount
          } else {
            changePriceA = ''
          }

          if (b.profile.isETF || b.profile.isMutualFund) {
            changePriceB = b.dailyPrice[this.priceType]?.priceChangeAmount
          } else if (b.profile.is529) {
            changePriceB = b.dailyPrice.regular.priceChangeAmount
          } else {
            changePriceB = ''
          }

          if (typeof changePriceA === 'undefined' || changePriceA === '') {
            changePriceA = TEMP_RANGE
          }
          if (typeof changePriceB === 'undefined' || changePriceB === '') {
            changePriceB = TEMP_RANGE
          }

          if (direction === 'asc') {
            return parseFloat(changePriceA) < parseFloat(changePriceB)
              ? LESS_THAN
              : GREATER_THAN
          } else {
            return parseFloat(changePriceB) < parseFloat(changePriceA)
              ? LESS_THAN
              : GREATER_THAN
          }
        })
    )
  }
}
